import { useState, useMemo, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Dropdown,
  Modal,
  Table
} from 'react-bootstrap';
import Badge from '../../components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import RevealDropdown, {
  RevealDropdownTrigger
} from '../../components/base/RevealDropdown';
import {
  useRawMaterials,
  useDeleteRawMaterial,
  useDeleteManyRawMaterials
} from '../../features/raw-materials/hooks/useRawMaterials.js';
import { http as redisHttp } from '../../features/jobs/api/http';

// ─── constants ───────────────────────────────────────────────────────
const STATUS_COLORS = {
  AVAILABLE: 'success',
  IN_USE: 'primary',
  CONSUMED: 'secondary'
};

const WIRE_TYPE_LABEL = {
  'SINGLE-WIRE': 'Solid',
  STRANDED: 'Stranded'
};

const PACKAGING_LABEL = {
  metal_reel: 'Metal Reel',
  octabin: 'Octabin',
  wooden_pallet: 'Wooden Pallet',
  other: 'Other'
};

// ─── columns ─────────────────────────────────────────────────────────
const buildColumns = (onDelete, onShowHistory) => [
  {
    accessorKey: 'supplier',
    header: 'Supplier',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold">{original.supplier}</span>
    ),
    meta: {
      headerProps: { style: { width: '12%' } },
      cellProps: { className: 'py-3 white-space-nowrap' }
    }
  },
  {
    id: 'delivery',
    header: 'Delivery',
    accessorFn: row => row.deliveryDate || '',
    cell: ({ row: { original } }) => (
      <div>
        <span>
          {original.deliveryDate
            ? new Date(original.deliveryDate).toLocaleDateString('ro-RO')
            : '-'}
        </span>
        {original.deliveryNote && (
          <span className="text-500 ms-1 fs-10">#{original.deliveryNote}</span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    accessorKey: 'wireType',
    header: 'Wire',
    cell: ({ row: { original } }) => (
      <div className="d-flex align-items-center gap-2">
        <Badge
          bg={original.wireType === 'STRANDED' ? 'info' : 'warning'}
          variant="phoenix"
          className="fs-10"
        >
          {WIRE_TYPE_LABEL[original.wireType] || original.wireType}
        </Badge>
        <span className="text-700 fs-10">
          {original.numberOfWires > 1
            ? `${original.numberOfWires}x${original.wireDiameter}`
            : `${original.wireDiameter} mm`}
        </span>
      </div>
    ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    accessorKey: 'section',
    header: 'Section',
    cell: ({ row: { original } }) =>
      original.section ? `${original.section} mm\u00B2` : '-',
    meta: {
      headerProps: { style: { width: '7%' }, className: 'text-end' },
      cellProps: { className: 'py-3 text-end' }
    }
  },
  {
    id: 'reelId',
    header: 'Reel / Pack',
    accessorFn: row => row.reelId || row.packNo || '',
    cell: ({ row: { original } }) => (
      <span className="font-monospace fs-10">
        {original.reelId || original.packNo || '-'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    accessorKey: 'packagingType',
    header: 'Packaging',
    cell: ({ row: { original } }) => (
      <span>
        {PACKAGING_LABEL[original.packagingType] || original.packagingType}
        {original.packagingModel && (
          <span className="text-500 ms-1 fs-10">
            ({original.packagingModel})
          </span>
        )}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    accessorKey: 'netWeight',
    header: 'Net (kg)',
    cell: ({ row: { original } }) => original.netWeight?.toFixed(2),
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-end' },
      cellProps: { className: 'py-3 text-end' }
    }
  },
  {
    accessorKey: 'availableWeight',
    header: 'Avail. (kg)',
    cell: ({ row: { original } }) => {
      const w = original.status === 'IN_USE' && original.liveWeight != null
        ? original.liveWeight
        : original.availableWeight;
      return (
        <span className={`fw-semibold${original.status === 'IN_USE' ? ' text-primary' : ''}`}>
          {w?.toFixed(2)}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-end' },
      cellProps: { className: 'py-3 text-end' }
    }
  },
  {
    id: 'machine',
    header: 'Machine',
    accessorFn: row => row.assignedTo?.machineId || '',
    cell: ({ row: { original } }) => {
      const mid = original.assignedTo?.machineId;
      if (!mid) return <span className="text-400">-</span>;
      return (
        <span className="fw-semibold text-uppercase text-primary">
          {mid}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '7%' }, className: 'text-center' },
      cellProps: { className: 'py-3 text-center' }
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row: { original } }) => (
      <Badge
        bg={STATUS_COLORS[original.status] || 'dark'}
        variant="phoenix"
      >
        {original.status}
      </Badge>
    ),
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-center' },
      cellProps: { className: 'py-3 text-center' }
    }
  },
  {
    id: 'action',
    cell: ({ row: { original } }) => (
      <RevealDropdownTrigger>
        <RevealDropdown>
          <Dropdown.Item onClick={() => onShowHistory(original)}>
            <FontAwesomeIcon icon="clock-rotate-left" className="me-2" />
            Traceability
          </Dropdown.Item>
          <Dropdown.Item
            className="text-danger"
            onClick={() => {
              if (window.confirm('Delete this raw material?')) {
                onDelete(original._id);
              }
            }}
          >
            <FontAwesomeIcon icon="trash" className="me-2" />
            Delete
          </Dropdown.Item>
        </RevealDropdown>
      </RevealDropdownTrigger>
    ),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-3' }
    }
  }
];

// ─── Traceability modal with live production info ────────────────────
const PHASE_LABELS = {
  unwinding: 'Unwinding',
  conductor_isolation: 'Conductor Isolation',
  pre_stranding: 'Pre-Stranding',
  rope: 'Rope',
  interior_mantle: 'Interior Mantle',
  armoring: 'Armoring',
  exterior_mantle: 'Exterior Mantle',
  coiling: 'Coiling'
};

const TraceabilityModal = ({ material, onHide }) => {
  const [liveData, setLiveData] = useState(null);

  // Fetch live production data when modal opens for an IN_USE material
  useEffect(() => {
    if (!material || material.status !== 'IN_USE') {
      setLiveData(null);
      return;
    }
    const machineId = material.assignedTo?.machineId;
    const taskId = material.assignedTo?.taskId;
    if (!machineId || !taskId) return;

    let cancelled = false;
    const fetchLive = () => {
      redisHttp
        .get(`/api/redis/queues/${machineId}`)
        .then(({ data }) => {
          if (cancelled) return;
          const groups = data?.groups || [];
          for (const g of groups) {
            for (const t of g.queue || []) {
              if (t.taskId === taskId) {
                setLiveData({
                  jobCode: g.jobCode,
                  productLabel: g.productLabel || g.name,
                  phase: t.phase,
                  status: t.status,
                  progress: t.progress,
                  plannedLength: t.plannedLength,
                  executedLength: t.executedLength,
                  materialExecution: t.materialExecution || [],
                  outputs: t.outputs || []
                });
                return;
              }
            }
          }
        })
        .catch(() => {});
    };
    fetchLive();
    const interval = setInterval(fetchLive, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [material]);

  if (!material) return null;

  const liveWeight =
    material.status === 'IN_USE' && material.liveWeight != null
      ? material.liveWeight
      : material.availableWeight;

  // Find live consumed kg for this material's spec from materialExecution
  let liveConsumedKg = null;
  if (liveData?.materialExecution?.length) {
    for (const me of liveData.materialExecution) {
      if ((me.material || '').toUpperCase() !== 'COPPER') continue;
      if (Math.abs(Number(me.wireDiameter) - material.wireDiameter) < 0.05) {
        liveConsumedKg = me.executed?.quantity_kg ?? 0;
        break;
      }
    }
  }

  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-8">
          Traceability: {material.reelId || material.packNo || '-'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-4 flex-wrap mb-3 fs-9">
          <div>
            <span className="text-700">Supplier:</span>{' '}
            <strong>{material.supplier}</strong>
          </div>
          <div>
            <span className="text-700">Spec:</span>{' '}
            <strong>
              {material.numberOfWires > 1
                ? `${material.numberOfWires}x${material.wireDiameter} mm`
                : `${material.wireDiameter} mm`}
            </strong>
          </div>
          <div>
            <span className="text-700">Net Weight:</span>{' '}
            <strong>{material.netWeight?.toFixed(2)} kg</strong>
          </div>
          <div>
            <span className="text-700">Available:</span>{' '}
            <strong>{liveWeight?.toFixed(2)} kg</strong>
          </div>
          <div>
            <Badge
              bg={STATUS_COLORS[material.status] || 'dark'}
              variant="phoenix"
            >
              {material.status}
            </Badge>
          </div>
        </div>

        {/* Live production section */}
        {material.status === 'IN_USE' && (
          <Card className="mb-3 border-primary">
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Badge bg="primary" variant="phoenix">LIVE</Badge>
                <span className="fw-semibold">
                  Active Production — {material.assignedTo?.machineId?.toUpperCase()}
                </span>
              </div>
              {liveData ? (
                <div className="fs-9">
                  <Row className="g-3">
                    <Col xs={6} md={3}>
                      <div className="text-700">Product</div>
                      <div className="fw-semibold">{liveData.productLabel || '-'}</div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-700">Phase</div>
                      <div className="fw-semibold">{PHASE_LABELS[liveData.phase] || liveData.phase}</div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-700">Progress</div>
                      <div className="fw-semibold">
                        {(liveData.executedLength || 0).toFixed(0)} / {(liveData.plannedLength || 0).toFixed(0)} m
                        <span className="text-500 ms-1">({(liveData.progress || 0).toFixed(0)}%)</span>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-700">Consumed</div>
                      <div className="fw-semibold text-danger">
                        {liveConsumedKg != null ? `${liveConsumedKg.toFixed(2)} kg` : '-'}
                      </div>
                    </Col>
                  </Row>
                  {liveData.outputs?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-700 me-2">Tokens:</span>
                      {liveData.outputs.map((tok, i) => (
                        <Badge
                          key={i}
                          bg={tok.status === 'ACTIVE' ? 'warning' : tok.status === 'DONE' ? 'success' : 'secondary'}
                          variant="phoenix"
                          className="me-1 font-monospace"
                        >
                          {tok.tokenId?.slice(-6) || '?'}
                          {tok.color ? ` (${tok.color})` : ''}
                          {' '}{(tok.producedLength || 0).toFixed(0)}m
                          {tok.status === 'ACTIVE' && ' ●'}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-500 fs-9">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading live data...
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Usage history */}
        {material.usageLog?.length > 0 ? (
          <Table striped bordered size="sm" className="fs-10 mb-0">
            <thead>
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Machine</th>
                <th>Token(s)</th>
                <th className="text-end">Length (m)</th>
                <th className="text-end">Consumed (kg)</th>
              </tr>
            </thead>
            <tbody>
              {material.usageLog.map((log, i) => (
                <tr key={i}>
                  <td>
                    {log.assignedAt
                      ? new Date(log.assignedAt).toLocaleString('ro-RO')
                      : '-'}
                  </td>
                  <td>
                    {log.releasedAt
                      ? new Date(log.releasedAt).toLocaleString('ro-RO')
                      : '-'}
                  </td>
                  <td className="text-uppercase fw-semibold">
                    {log.machineId || '-'}
                  </td>
                  <td>
                    <span className="font-monospace">
                      {log.tokenId || '-'}
                    </span>
                  </td>
                  <td className="text-end font-monospace">
                    {log.lengthProduced?.toFixed(2) || '0.00'}
                  </td>
                  <td className="text-end font-monospace fw-semibold">
                    {log.weightUsed?.toFixed(2) || '0.00'}
                  </td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td colSpan={4} className="text-end">
                  Total Consumed:
                </td>
                <td className="text-end font-monospace">
                  {material.usageLog
                    .reduce((s, l) => s + (l.lengthProduced || 0), 0)
                    .toFixed(2)}
                </td>
                <td className="text-end font-monospace">
                  {material.usageLog
                    .reduce((s, l) => s + (l.weightUsed || 0), 0)
                    .toFixed(2)}
                </td>
              </tr>
            </tbody>
          </Table>
        ) : material.status !== 'IN_USE' ? (
          <div className="text-center text-700 py-4">
            No usage history.
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

// ─── component ───────────────────────────────────────────────────────
const RawMaterialList = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [wireTypeFilter, setWireTypeFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [historyMaterial, setHistoryMaterial] = useState(null);

  const { data, isLoading } = useRawMaterials();
  const deleteMutation = useDeleteRawMaterial();
  const deleteManyMutation = useDeleteManyRawMaterials();

  const materials = data?.data || [];

  const suppliers = useMemo(() => {
    const set = new Set(materials.map(m => m.supplier).filter(Boolean));
    return Array.from(set).sort();
  }, [materials]);

  const filtered = useMemo(() => {
    return materials.filter(m => {
      if (statusFilter && m.status !== statusFilter) return false;
      if (wireTypeFilter && m.wireType !== wireTypeFilter) return false;
      if (supplierFilter && m.supplier !== supplierFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (m.supplier || '').toLowerCase().includes(q) ||
          (m.reelId || '').toLowerCase().includes(q) ||
          (m.packNo || '').toLowerCase().includes(q) ||
          (m.deliveryNote || '').toLowerCase().includes(q) ||
          String(m.wireDiameter).includes(q) ||
          String(m.numberOfWires).includes(q)
        );
      }
      return true;
    });
  }, [materials, search, statusFilter, wireTypeFilter, supplierFilter]);

  const stats = useMemo(() => {
    const available = filtered.filter(m => m.status === 'AVAILABLE');
    return {
      total: filtered.length,
      available: available.length,
      totalWeight: filtered.reduce((s, m) => s + (m.netWeight || 0), 0),
      availableWeight: available.reduce(
        (s, m) => s + (m.availableWeight || 0),
        0
      )
    };
  }, [filtered]);

  const columns = useMemo(
    () => buildColumns(id => deleteMutation.mutate(id), m => setHistoryMaterial(m)),
    [deleteMutation]
  );

  const table = useAdvanceTable({
    data: filtered,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true,
    selection: true
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedIds = selectedRows.map(r => r.original._id);

  const handleBulkDelete = async () => {
    if (
      !selectedIds.length ||
      !window.confirm(`Delete ${selectedIds.length} selected items?`)
    )
      return;
    await deleteManyMutation.mutateAsync(selectedIds);
    table.resetRowSelection();
  };

  return (
    <div className="mb-9 mt-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Raw Materials</h2>
          <p className="text-700 mb-0">
            Copper raw materials from supplier packing lists
          </p>
        </div>
        <Link to="/apps/material-management/upload-packing-list">
          <Button variant="primary">
            <FontAwesomeIcon icon="upload" className="me-2" />
            Add Raw Materials
          </Button>
        </Link>
      </div>

      {/* Stats bar */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <Card className="h-100">
            <Card.Body className="py-3 text-center">
              <div className="fs-7 fw-bold text-primary">{stats.total}</div>
              <div className="text-700 fs-9">Total Items</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="h-100">
            <Card.Body className="py-3 text-center">
              <div className="fs-7 fw-bold text-success">{stats.available}</div>
              <div className="text-700 fs-9">Available</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="h-100">
            <Card.Body className="py-3 text-center">
              <div className="fs-7 fw-bold text-warning">
                {stats.totalWeight.toFixed(0)} kg
              </div>
              <div className="text-700 fs-9">Total Weight</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="h-100">
            <Card.Body className="py-3 text-center">
              <div className="fs-7 fw-bold text-info">
                {stats.availableWeight.toFixed(0)} kg
              </div>
              <div className="text-700 fs-9">Available Weight</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter bar */}
      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-center">
            <Col xs={12} sm={3}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </Col>
            <Col xs="auto">
              <Form.Select
                size="sm"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">All status</option>
                <option value="AVAILABLE">Available</option>
                <option value="IN_USE">In Use</option>
                <option value="CONSUMED">Consumed</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Select
                size="sm"
                value={wireTypeFilter}
                onChange={e => setWireTypeFilter(e.target.value)}
              >
                <option value="">All wire types</option>
                <option value="SINGLE-WIRE">Solid (Single Wire)</option>
                <option value="STRANDED">Stranded</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Select
                size="sm"
                value={supplierFilter}
                onChange={e => setSupplierFilter(e.target.value)}
              >
                <option value="">All suppliers</option>
                {suppliers.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto" className="ms-auto d-flex align-items-center gap-3">
              {selectedIds.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={deleteManyMutation.isPending}
                >
                  {deleteManyMutation.isPending ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <FontAwesomeIcon icon="trash" className="me-1" />
                  )}
                  Delete {selectedIds.length}
                </Button>
              )}
              <span className="text-700 fs-9">{filtered.length} items</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-6">
          <Spinner
            animation="border"
            variant="primary"
            size="sm"
            className="me-2"
          />
          <span className="text-700">Loading...</span>
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-700 mb-2">No raw materials found</p>
            <p className="text-700 fs-9">
              Upload a packing list PDF to import raw materials automatically.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <AdvanceTableProvider {...table}>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table border-top border-200 fs-9'
              }}
            />
            <AdvanceTableFooter pagination className="py-3" />
          </div>
        </AdvanceTableProvider>
      )}

      {/* Traceability Modal */}
      <TraceabilityModal
        material={historyMaterial}
        onHide={() => setHistoryMaterial(null)}
      />
    </div>
  );
};

export default RawMaterialList;
